import React, { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { applicationStore, userInfoStore } from '@pascal/pascal-core/shared/core/store';
import {
  exchangeAmountByRate,
  freeBetStore,
  iframeService,
  useTranslation,
} from '@pascal/pascal-core';
import { doBetStore } from '../../store/doBetStore';
import { winningAmountStore } from '../../store/winningAmountStore';
import { BET_TYPES, BET_TYPES_NAMES, OWNER_T_KEYS } from '../../enums/cardOwner';
import { ChipSection } from '../chipSection';
import { betResponseCodeByName } from '../../enums/betResponseCode';

export const FieldCowerSection = observer(() => {
  const { selectedChip, updateBetsModel, liveBetsInfo, lastBetModel, handleNotificationCode } =
    doBetStore;
  const { currencySymbolOrCode, userId } = userInfoStore;
  const { isDemo } = applicationStore;
  const { setLastWin } = winningAmountStore;
  const { activeFreeBet } = freeBetStore;
  const t = useTranslation();

  const handleDoChip = useCallback(
    (type) => {
      if (
        activeFreeBet.id &&
        lastBetModel.betModel &&
        activeFreeBet.remainingCount - lastBetModel.betModel.length === 0
      ) {
        handleNotificationCode(betResponseCodeByName.NotFreeChip);
        return;
      }
      if (userId || isDemo) {
        setLastWin(0);
        updateBetsModel({
          type,
          amount: selectedChip,
        });
      } else {
        iframeService.sendPgLogin();
      }
    },
    [
      activeFreeBet.id,
      activeFreeBet.remainingCount,
      isDemo,
      lastBetModel.betModel,
      selectedChip,
      setLastWin,
      updateBetsModel,
      handleNotificationCode,
      userId,
    ]
  );

  const memoExchangeAmount = useCallback(
    (currentLiveBetsName) => {
      if (liveBetsInfo[currentLiveBetsName]) {
        return liveBetsInfo[currentLiveBetsName]?.isOnlyUser
          ? liveBetsInfo[currentLiveBetsName].totalBetAmount
          : exchangeAmountByRate({
              amount: liveBetsInfo[currentLiveBetsName].totalBetAmount,
              coinAmount: liveBetsInfo[currentLiveBetsName]?.totalCoinAmount,
              userId: true,
              currencyCode: null,
            }).amount || 0;
      }
      return 0;
    },
    [liveBetsInfo]
  );

  return (
    <div className='football-field-roof'>
      <div className='football-field-roof-column left'>
        <div className='football-field-roof-column-inner left'>
          <div className='football-field-roof-column-block left'>
            <div
              className='football-field-roof-left-imitation'
              onClick={() => handleDoChip(BET_TYPES.home)}
            >
              <span className='field-roof-text home'>{t(OWNER_T_KEYS.HomeWin)}</span>
              <span className='field-roof-chip-count'>1:1</span>
              <div className='field-roof-chip-place' />
              <ChipSection position='left' betType={BET_TYPES.home} />
            </div>
          </div>
        </div>
        <div className='football-field-info left'>
          <div className='football-field-info-column'>
            <div className='football-field-user-row'>
              <i className='football-field-user-icon icon-user-count' />
              <span className='football-field-user-count'>
                {liveBetsInfo[BET_TYPES_NAMES[BET_TYPES.home]]
                  ? liveBetsInfo[BET_TYPES_NAMES[BET_TYPES.home]].userCount
                  : 0}
              </span>
            </div>
            <p className='football-field-money'>
              {memoExchangeAmount(BET_TYPES_NAMES[BET_TYPES.home])}{' '}
              <span>{!isDemo ? currencySymbolOrCode : null}</span>
            </p>
          </div>
          <div className='football-field-info-column circle'>
            {liveBetsInfo[BET_TYPES_NAMES[BET_TYPES.home]]
              ? liveBetsInfo[BET_TYPES_NAMES[BET_TYPES.home]].userPercentage
              : 0}
            %
          </div>
        </div>
      </div>
      <div className='football-field-roof-column right'>
        <div className='football-field-roof-column-inner right'>
          <div className='football-field-roof-column-block right'>
            <div
              className='football-field-roof-center-imitation'
              onClick={() => handleDoChip(BET_TYPES.draw)}
            >
              <ChipSection position='center' betType={BET_TYPES.draw} />
              <div className='field-roof-chip-place' />
              <span className='field-roof-chip-count'>11:1</span>
              <span className='field-roof-text draw'>{t(OWNER_T_KEYS.Draw)}</span>
              <div className='field-roof-draw-info'>
                <div className='field-roof-draw-info-column'>
                  <i className='field-roof-draw-user icon-user-count' />
                  <span className='field-roof-draw-info-text'>
                    {liveBetsInfo[BET_TYPES_NAMES[BET_TYPES.draw]]
                      ? liveBetsInfo[BET_TYPES_NAMES[BET_TYPES.draw]].userCount
                      : 0}
                  </span>
                </div>
                <div className='field-roof-draw-info-column'>
                  <span className='field-roof-draw-info-text'>
                    {liveBetsInfo[BET_TYPES_NAMES[BET_TYPES.draw]]
                      ? liveBetsInfo[BET_TYPES_NAMES[BET_TYPES.draw]].userPercentage
                      : 0}
                    %
                  </span>
                </div>
              </div>
            </div>
            <div
              className='football-field-roof-right-imitation'
              onClick={() => handleDoChip(BET_TYPES.away)}
            >
              <ChipSection position='right' betType={BET_TYPES.away} />
              <div className='field-roof-chip-place' />
              <span className='field-roof-chip-count'>1:1</span>
              <span className='field-roof-text away'>{t(OWNER_T_KEYS.AwayWin)}</span>
            </div>
          </div>
        </div>
        <div className='football-field-info right'>
          <div className='football-field-info-column circle'>
            {liveBetsInfo[BET_TYPES_NAMES[BET_TYPES.away]]
              ? liveBetsInfo[BET_TYPES_NAMES[BET_TYPES.away]].userPercentage
              : 0}
            %
          </div>
          <div className='football-field-info-column'>
            <div className='football-field-user-row'>
              <i className='football-field-user-icon icon-user-count' />
              <span className='football-field-user-count'>
                {liveBetsInfo[BET_TYPES_NAMES[BET_TYPES.away]]
                  ? liveBetsInfo[BET_TYPES_NAMES[BET_TYPES.away]].userCount
                  : 0}
              </span>
            </div>
            <p className='football-field-money'>
              {memoExchangeAmount(BET_TYPES_NAMES[BET_TYPES.away])}{' '}
              <span>{!isDemo ? currencySymbolOrCode : null}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});
